import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  ERROR_CODES,
  pickBilingual,
  type PortalReportVersionView,
  type PortalReportView,
  type PortalWorkOrderView,
} from '@lims/shared-types';
import { ApiError } from '../api/client.js';
import { formatDate, formatDateTime } from '../components/format.js';
import {
  ActionButton,
  Alert,
  DataTable,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  ReadOnlyField,
  StatusBadge,
  type DataColumn,
} from '../components/ui/index.js';
import { currentLocale } from '../i18n/index.js';
import { portalApi } from './portal-api.js';
import { PORTAL_QUERY_KEY } from './PortalSession.js';

/**
 * The client's requests (work orders) and released reports — Phase 11.
 *
 * Everything shown is what the server's allow-lists return for THIS account:
 * no internal note, no person, no draft, no hold reason. A report file that
 * cannot be downloaded says only that it is not available at present — the
 * reason is the laboratory's to give, never the page's (contract P).
 */

const linkClass = 'rounded-sm font-medium text-primary-700 hover:underline';

function dateText(
  value: string | null,
  locale: ReturnType<typeof currentLocale>,
  fallback: string,
): string {
  return formatDate(value, { locale }) ?? fallback;
}

export function PortalWorkOrdersPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const [page, setPage] = useState(1);

  const list = useQuery({
    queryKey: [...PORTAL_QUERY_KEY, 'work-orders', page],
    queryFn: () => portalApi.workOrders({ page, pageSize: 25 }),
  });

  const columns: readonly DataColumn<PortalWorkOrderView>[] = [
    {
      key: 'number',
      header: t('portal.requests.number'),
      render: (row) => (
        <Link to={`/portal/requests/${row.id}`} className={linkClass}>
          <bdi dir="ltr">{row.number}</bdi>
        </Link>
      ),
    },
    {
      key: 'subject',
      header: t('portal.requests.subject'),
      render: (row) => (
        <div className="min-w-0">
          <span className="block">{row.subject ?? t('common.notRecorded')}</span>
          {row.project ? (
            <span className="block text-caption text-default-600">
              {row.project.name}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'requestDate',
      header: t('portal.requests.requestDate'),
      render: (row) => dateText(row.requestDate, locale, t('common.notRecorded')),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (row) => (
        <div className="flex flex-col gap-1">
          <StatusBadge label={t(`status.workOrder.${row.status}`)} tone={row.cancelled ? 'neutral' : 'info'} />
          <span className="text-caption text-default-600">
            {t('portal.requests.progress', { completed: row.progress.completed, total: row.progress.total })}
          </span>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('portal.requests.title')} subtitle={t('portal.requests.subtitle')} />
      <DataTable
        label={t('portal.requests.title')}
        columns={columns}
        rows={list.data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        emptyContent={t('portal.requests.none')}
        page={page}
        totalPages={list.data?.meta.totalPages ?? 1}
        onPageChange={setPage}
      />
    </>
  );
}

export function PortalWorkOrderPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const { id = '' } = useParams<{ id: string }>();

  const item = useQuery({
    queryKey: [...PORTAL_QUERY_KEY, 'work-order', id],
    queryFn: () => portalApi.workOrder(id),
    enabled: id !== '',
  });

  if (item.isLoading) return <LoadingState label={t('common.loading')} />;
  if (item.error || !item.data) return <ErrorState error={item.error} onRetry={() => void item.refetch()} />;
  const order = item.data;

  return (
    <>
      <PageHeader
        title={order.number}
        subtitle={order.subject ?? undefined}
        breadcrumbs={[{ label: t('portal.nav.requests'), to: '/portal' }, { label: order.number }]}
      />
      <div className="flex flex-col gap-gutter">
        <Panel title={t('portal.requests.details')}>
          <div className="grid gap-stack sm:grid-cols-2">
            <ReadOnlyField label={t('common.status')}>{t(`status.workOrder.${order.status}`)}</ReadOnlyField>
            <ReadOnlyField label={t('portal.requests.progressLabel')}>
              {t('portal.requests.progress', { completed: order.progress.completed, total: order.progress.total })}
            </ReadOnlyField>
            <ReadOnlyField label={t('portal.requests.project')}>
              {order.project ? order.project.name : t('common.notRecorded')}
            </ReadOnlyField>
            <ReadOnlyField label={t('portal.requests.requestDate')}>{dateText(order.requestDate, locale, t('common.notRecorded'))}</ReadOnlyField>
            <ReadOnlyField label={t('portal.requests.dueDate')}>{dateText(order.dueDate, locale, t('common.notRecorded'))}</ReadOnlyField>
            <ReadOnlyField label={t('portal.requests.purchaseOrder')}>
              <bdi dir="ltr">{order.purchaseOrderNumber ?? t('common.notRecorded')}</bdi>
            </ReadOnlyField>
            <ReadOnlyField label={t('portal.requests.clientReference')}>
              <bdi dir="ltr">{order.clientReference ?? t('common.notRecorded')}</bdi>
            </ReadOnlyField>
          </div>
        </Panel>

        <Panel title={t('portal.requests.services')}>
          {order.services.length === 0 ? (
            <p className="text-body text-default-600">{t('portal.requests.noServices')}</p>
          ) : (
            <ul className="flex flex-col gap-stack">
              {order.services.map((service, index) => (
                <li key={index} className="flex flex-wrap items-center justify-between gap-inline border-b border-default-100 pb-2">
                  <span className="min-w-0">
                    {pickBilingual(service.description, locale) ?? t('common.notRecorded')}{' '}
                    <span className="text-caption text-default-600">
                      {t('portal.requests.quantity', { quantity: service.quantity })}
                    </span>
                  </span>
                  <StatusBadge label={t(`workOrderServiceState.${service.state}`)} tone="neutral" />
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title={t('portal.nav.reports')}>
          {order.reportIds.length === 0 ? (
            <p className="text-body text-default-600">{t('portal.requests.noReports')}</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {order.reportIds.map((reportId, index) => (
                <li key={reportId}>
                  <Link to={`/portal/reports/${reportId}`} className={linkClass}>
                    {t('portal.requests.openReport', { number: index + 1 })}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}

export function PortalReportsPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const list = useQuery({
    queryKey: [...PORTAL_QUERY_KEY, 'reports', page],
    queryFn: () => portalApi.reports({ page, pageSize: 25 }),
  });

  const columns: readonly DataColumn<PortalReportView>[] = [
    {
      key: 'number',
      header: t('portal.reports.number'),
      render: (row) => (
        <Link to={`/portal/reports/${row.id}`} className={linkClass}>
          <bdi dir="ltr">{row.reportNumber ?? t('portal.reports.unnumbered')}</bdi>
        </Link>
      ),
    },
    {
      key: 'workOrder',
      header: t('portal.reports.request'),
      render: (row) => <bdi dir="ltr">{row.workOrder.number}</bdi>,
    },
    {
      key: 'current',
      header: t('portal.reports.currentVersion'),
      render: (row) =>
        row.current
          ? t('portal.reports.version', { number: row.current.versionNumber })
          : t('portal.reports.noCurrent'),
    },
  ];

  return (
    <>
      <PageHeader title={t('portal.reports.title')} subtitle={t('portal.reports.subtitle')} />
      <DataTable
        label={t('portal.reports.title')}
        columns={columns}
        rows={list.data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        emptyContent={t('portal.reports.none')}
        page={page}
        totalPages={list.data?.meta.totalPages ?? 1}
        onPageChange={setPage}
      />
    </>
  );
}

function saveFile(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function VersionRow({ report, version }: { readonly report: PortalReportView; readonly version: PortalReportVersionView }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const [isDownloading, setDownloading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const download = async () => {
    setError(null);
    setDownloading(true);
    try {
      const blob = await portalApi.reportFile(report.id, version.versionNumber);
      saveFile(blob, `${report.reportNumber ?? report.id}-v${version.versionNumber}.pdf`);
    } catch (caught) {
      setError(caught);
    } finally {
      setDownloading(false);
    }
  };

  const refusedAsUnavailable =
    error instanceof ApiError && error.code === ERROR_CODES.ORIGINAL_NOT_AVAILABLE;

  return (
    <li className="flex flex-col gap-2 border-s-2 border-default-200 ps-3">
      <div className="flex flex-wrap items-center gap-inline">
        <span className="text-body font-medium text-default-900">
          {t('portal.reports.version', { number: version.versionNumber })}
        </span>
        <StatusBadge label={t(`status.reportVersion.${version.status}`)} tone="neutral" />
        {report.current?.versionNumber === version.versionNumber ? (
          <StatusBadge label={t('portal.reports.currentBadge')} tone="info" />
        ) : null}
      </div>
      <p className="text-caption text-default-600">
        {t('portal.reports.issuedOn', { date: formatDate(version.issuedAt, { locale }) ?? t('common.notRecorded') })}
        {' · '}
        {t(`reportLanguage.${version.language}`)}
        {' · '}
        {t('portal.reports.releasedAt', { date: formatDateTime(version.releasedAt, { locale }) ?? t('common.notRecorded') })}
      </p>
      {version.testTitles.length > 0 ? (
        <p className="text-caption text-default-700">
          {version.testTitles.map((title) => pickBilingual(title, locale)).filter(Boolean).join(' · ')}
        </p>
      ) : null}
      {version.fileAvailable ? (
        <div>
          <ActionButton priority="secondary" size="sm" isLoading={isDownloading} onPress={() => void download()}>
            {t('portal.reports.download')}
          </ActionButton>
        </div>
      ) : (
        <p className="text-caption text-default-700">{t('portal.reports.notAvailable')}</p>
      )}
      {refusedAsUnavailable ? (
        <Alert tone="warning" title={t('portal.reports.notAvailable')} />
      ) : error ? (
        <ErrorState error={error} />
      ) : null}
    </li>
  );
}

export function PortalReportPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();

  const item = useQuery({
    queryKey: [...PORTAL_QUERY_KEY, 'report', id],
    queryFn: () => portalApi.report(id),
    enabled: id !== '',
  });

  if (item.isLoading) return <LoadingState label={t('common.loading')} />;
  if (item.error || !item.data) return <ErrorState error={item.error} onRetry={() => void item.refetch()} />;
  const report = item.data;
  const title = report.reportNumber ?? t('portal.reports.unnumbered');

  return (
    <>
      <PageHeader
        title={title}
        subtitle={t('portal.reports.forRequest', { number: report.workOrder.number })}
        breadcrumbs={[{ label: t('portal.nav.reports'), to: '/portal/reports' }, { label: title }]}
      />
      <div className="flex flex-col gap-gutter">
        {report.newerVersionNotice ? (
          <Alert tone="warning" title={t('portal.reports.newerVersionTitle')} description={t('portal.reports.newerVersionBody')} />
        ) : null}
        <Alert tone="info" title={t('portal.reports.approvalNotPassTitle')} description={t('portal.reports.approvalNotPassBody')} />
        <Panel title={t('portal.reports.versions')}>
          <ul className="flex flex-col gap-stack" data-testid="portal-report-versions">
            {report.versions.map((version) => (
              <VersionRow key={version.versionNumber} report={report} version={version} />
            ))}
          </ul>
        </Panel>
      </div>
    </>
  );
}
